<template>
  <div
    class="LayoutDashboard"
    @dragenter.prevent.stop="handleDragStart"
    @dragover.prevent.stop="handleDragStart"
    @dragleave.prevent.stop="handleDragLeave"
    @drop.prevent.stop="handleDragUpload"
  >
    <UploadDragScreen
      v-if="isDragging"
      class="LayoutDashboard__upload-screen"
    />
    <div
      class="LayoutDashboard__layout-container"
      :data-sidebar-open="isSidebarGridOpen"
    >
      <TheSidebar
        :isOpen="isSidebarOpen"
        @toggle="toggleSidebar"
        @afterLeave="onSidebarAfterLeave"
      />

      <div
        :class="[
          'LayoutDashboard__workspace',
          {
            'LayoutDashboard__workspace--split': isPreviewOpen,
            'LayoutDashboard__workspace--show-upload': isDragging,
          },
        ]"
      >
        <div id="LayoutDashboard__main" class="LayoutDashboard__main">
          <TheTopbar
            :showActionButtons="isMobileView || !isSidebarGridOpen"
            @toggleSidebar="toggleSidebar"
          />

          <div class="LayoutDashboard__body">
            <div
              :class="[
                'LayoutDashboard__container',
                {
                  'LayoutDashboard__container--no-padding': noContainerPadding,
                },
              ]"
            >
              <div
                :class="[
                  'LayoutDashboard__content',
                  { 'LayoutDashboard__content--show-footer': showFooter },
                ]"
              >
                <RouterView />
              </div>

              <div v-if="showFooter" class="LayoutDashboard__footer">
                Ensure your questions follow Foundever's security terms for a
                safe, productive workspace. EverGPT may make mistakes, so
                double-check important information.
              </div>
            </div>
          </div>
        </div>

        <div
          ref="previewWrapRef"
          class="LayoutDashboard__preview-wrap"
          :class="{
            'LayoutDashboard__preview-wrap--open': isPreviewOpen,
          }"
        >
          <PreviewPanel v-if="isPreviewOpen" />
        </div>
      </div>
    </div>
  </div>

  <ModalAnnouncement />
  <UINotification />
</template>

<script lang="ts" setup>
import { computed, nextTick, onMounted, ref, watch, onUnmounted } from 'vue'

import { useWindowSize } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { useRoute } from 'vue-router'

import { useEventListeners } from '@/composables/useEventListeners'
import { useMsClarity } from '@/plugins/clarity'
import { useAppStore } from '@/store/app'
import { useFileStore } from '@/store/file'
import { usePreviewStore } from '@/store/preview'
import { useSocketEventsStore } from '@/store/socketEvents'
import { isExternalFileDrag } from '@/utils/isExternalFileDrag'
import ModalAnnouncement from '@/views/components/modals/ModalAnnouncement'
import TheSidebar from '@/views/components/TheSidebar'

import PreviewPanel from '../components/preview/PreviewPanel.vue'
import TheTopbar from '../components/TheTopbar/TheTopbar.vue'
import UINotification from '../components/ui/notifications/UINotification.vue'
import UploadDragScreen from '../components/upload/UploadDragScreen.vue'

const route = useRoute()
const showFooter = computed(() => route.matched.some((r) => r.meta.showFooter))
const noContainerPadding = computed(() =>
  route.matched.some((r) => r.meta.noContainerPadding),
)

const previewStore = usePreviewStore()
const { isOpen: isPreviewOpen } = storeToRefs(previewStore)
const previewWrapRef = ref<HTMLElement | null>(null)

const { width } = useWindowSize()
const isMobileView = computed(() => width.value < 960)
// Factor in the preview panel's current state so a fresh setup (e.g. on HMR,
// where this component re-initializes but the preview store keeps its state)
// doesn't start the sidebar "open" and then immediately fake-close it without
// ever running the close transition, leaving isSidebarGridOpen stuck at true.
const isSidebarOpen = ref(!isMobileView.value && !isPreviewOpen.value)
const isSidebarGridOpen = ref(!isMobileView.value && !isPreviewOpen.value)

const toggleSidebar = () => {
  const willOpen = !isSidebarOpen.value
  isSidebarOpen.value = willOpen
  if (willOpen) {
    isSidebarGridOpen.value = true
  }
}

const onSidebarAfterLeave = () => {
  if (!isSidebarOpen.value) {
    isSidebarGridOpen.value = false
  }
}

watch(isMobileView, (isMobile) => {
  if (isMobile) return
  if (!isPreviewOpen.value) {
    // Back to desktop with no preview: show the sidebar and reserve its column.
    isSidebarOpen.value = true
    isSidebarGridOpen.value = true
  } else {
    // Preview is open, so the sidebar stays hidden — don't reserve its grid
    // column (nothing would run onSidebarAfterLeave to release it later).
    isSidebarGridOpen.value = false
  }
})

const fileStore = useFileStore()
const { isDragging } = storeToRefs(fileStore)

watch(
  isPreviewOpen,
  (open) => {
    if (!open && previewWrapRef.value) {
      previewWrapRef.value.style.removeProperty('flex')
    }
  },
  { flush: 'post' },
)

// Close preview on any route change. onBeforeRouteLeave only runs when leaving
// the route that rendered this component (the parent "/" route), not when
// navigating between dashboard children (e.g. Chat → Home), so we watch route.
watch(
  () => route.fullPath,
  () => {
    previewStore.closePreview()
  },
)
// Not `immediate: true`: the initial refs above already account for
// `isPreviewOpen` at setup time, so this only needs to react to later toggles
// (e.g. the user opening the preview panel while the sidebar is visible).
watch(isPreviewOpen, (val) => {
  if (!val) return
  if (isSidebarOpen.value) {
    // The sidebar is visible: closing it runs the leave transition, and
    // onSidebarAfterLeave then collapses the grid column. Leave
    // isSidebarGridOpen true so the column stays reserved during the animation.
    isSidebarOpen.value = false
  } else {
    // The sidebar is already hidden, so no leave transition (and no
    // onSidebarAfterLeave) fires. Collapse the grid column directly instead of
    // leaving an empty sidebar-width gap next to the preview.
    isSidebarGridOpen.value = false
  }
})

// Toggling the preview changes flex layout and closes the sidebar; reflow resets
// scroll positions. Capture before paint (sync) and restore after layout settles.
watch(
  isPreviewOpen,
  () => {
    const main = document.querySelector('#LayoutDashboard__main')
    const chatArea = main?.querySelector<HTMLElement>('.ChatArea')
    const savedChatScroll = chatArea?.scrollTop ?? 0
    const savedWindowX = window.scrollX
    const savedWindowY = window.scrollY

    nextTick(() => {
      nextTick(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const el = document
              .querySelector('#LayoutDashboard__main')
              ?.querySelector<HTMLElement>('.ChatArea')
            if (el) el.scrollTop = savedChatScroll
            window.scrollTo(savedWindowX, savedWindowY)
          })
        })
      })
    })
  },
  { flush: 'sync' },
)

const { isNotAuthorisedPage, allowUploads } = storeToRefs(useAppStore())

// Register keyboard events
useEventListeners().registerNewChatKeyboardEvent()
useEventListeners().initKeyboardEvents()

// Prevent default CTRL+N behavior
const handleGlobalKeyDown = (event: KeyboardEvent) => {
  if (event.ctrlKey && event.key.toLowerCase() === 'n') {
    event.preventDefault()
  }
}

onMounted(() => {
  // Initialize Clarity after app is mounted
  useMsClarity()

  // Do nothing if user is not authorised
  if (isNotAuthorisedPage.value) return

  window.addEventListener('keydown', handleGlobalKeyDown)

  // Initialize socket events
  useSocketEventsStore().initSocketEvents()
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleGlobalKeyDown)
})

const handleDragStart = (event: DragEvent) => {
  if (!allowUploads.value) return
  if (!isExternalFileDrag(event)) return
  isDragging.value = true
}

const handleDragLeave = (event: DragEvent) => {
  if (!allowUploads.value) return
  if (!isExternalFileDrag(event)) return
  const rect = (event.currentTarget as HTMLElement).getBoundingClientRect()
  const x = event.clientX
  const y = event.clientY

  if (x <= rect.left || x >= rect.right || y <= rect.top || y >= rect.bottom) {
    isDragging.value = false
  }
}

const handleDragUpload = (event: DragEvent) => {
  if (!allowUploads.value) return
  fileStore.handleDragUpload(event)
}
</script>

<style lang="scss" scoped>
.LayoutDashboard {
  width: 100%;
  background-color: var(--bg-primary);
  position: relative;

  .LayoutDashboard__upload-screen {
    position: absolute;
    z-index: 1;
    width: 100%;

    @include md {
      margin-left: $sidebar-width;
      width: calc(100% - #{$sidebar-width});
    }
  }
  .LayoutDashboard__layout-container {
    display: grid;
    // `minmax(0, …)`: plain `1fr` won't shrink below wide content (admin tabs).
    grid-template-columns: minmax(0, 1fr);

    // `TheSidebar` is `position: fixed` on mobile (out of grid flow). From `md` up
    // it is `position: relative` in the first grid column; the layout shell is
    // viewport-locked so only the main content column scrolls.
    @include md {
      height: 100vh;
      overflow: hidden;
      // `1fr` min track size is min-content; wide pages would grow the column
      // past the viewport and clip instead of scrolling inside the workspace.
      grid-template-columns: 0 minmax(0, 1fr);
    }

    &[data-sidebar-open='true'] {
      @include md {
        grid-template-columns: var(--sidebar-width) minmax(0, 1fr);
      }
    }
  }

  .LayoutDashboard__workspace {
    width: 100%;
    height: 100vh;
    min-height: 100%;
    // Let the grid track cap width so inner overflow:auto regions can scroll.
    min-width: 0;
  }

  .LayoutDashboard__workspace--show-upload {
    filter: blur(1rem);
  }

  /* Persistent split row so the main column and preview column can animate together */
  .LayoutDashboard__workspace--split {
    display: flex;
    flex-direction: row;
    height: 100vh;
    min-height: 0;

    > .LayoutDashboard__main {
      flex: 1 1 0%;
      min-width: 0;
      max-width: 100%;
    }

    .LayoutDashboard__preview-wrap {
      flex: 0 0 0%;
      max-width: 0;
      min-width: 0;
      display: flex;
      flex-direction: column;
      min-height: 0;
      overflow: hidden;
      pointer-events: none;
      transition:
        flex-basis 0.42s cubic-bezier(0.22, 1, 0.36, 1),
        max-width 0.42s cubic-bezier(0.22, 1, 0.36, 1),
        opacity 0.28s ease;

      opacity: 0;

      &--open {
        flex: 0 0 50%;
        max-width: 70%;
        min-width: 15.625rem;
        opacity: 1;
        pointer-events: auto;
      }
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .LayoutDashboard__workspace--split .LayoutDashboard__preview-wrap {
      transition: none;
    }
  }

  .LayoutDashboard__main {
    position: relative;
    display: flex;
    flex-direction: column;
    border-top-left-radius: 0.5rem;
    border-top-right-radius: 0.5rem;
    width: 100%;
    height: 100vh;
    min-height: 100%;
  }

  .LayoutDashboard__body {
    flex: 1;
    min-height: 0;
    min-width: 0;
  }

  .LayoutDashboard__container {
    display: flex;
    flex-direction: column;
    align-items: center;
    height: calc(100vh - $topbar-height);
    width: 100%;
    margin-top: $topbar-height;
    padding: 0 1rem 1rem 1rem;

    @include md {
      padding: 0 3rem 1rem;
    }

    &.LayoutDashboard__container--no-padding {
      padding: 0;
    }
  }

  .LayoutDashboard__content {
    @include customScrollbar;
    @include customScrollbarHorizontal;
    background-color: var(--bg-primary);
    height: 100%;
    width: 100%;
    padding: 0 0.5rem;

    @include md {
      min-height: 0;
    }

    &.LayoutDashboard__content--show-footer {
      height: calc(100% - 56px);
    }
  }

  .LayoutDashboard__footer {
    color: var(--text-tertiary);
    font-size: 0.625rem;
    line-height: 1rem;
    padding-top: 0.5rem;
    margin-top: auto;
    margin-right: 1rem;
    text-align: center;

    @include sm {
      padding-top: 1.5rem;
    }
  }
}
</style>
